import { useState, type FormEvent } from "react"
import { ArrowRight, MarkerPin01 } from "@untitledui/icons"
import { toast } from "sonner"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import type { TeamType } from "@/lib/api/types"
import { USStateSelect } from "@/components/ui/us-state-select"
import { formatPhone } from "@/lib/utils/phone"
import { formatZip } from "@/lib/utils/zip"
import { useOnboarding } from "@/lib/onboarding/onboarding-context"
import { companyLocationSchema } from "@/lib/onboarding/schemas"
import {
  Field,
  PrimaryButton,
  StaggerField,
  StepActions,
  StepLayout,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "./onboarding-shell"
import { OnboardingContextPanel } from "./context-panel"
import { cx } from "@/lib/utils/cx"

// Step 2b — Location. The team is actually created here, in a single API
// call that combines the identity from step 2a with the address. If 2a was
// skipped (defensive guard for the `Back → forward` edge case) we bounce
// the user back rather than POST a half-formed team.
//
// Address autocomplete is intentionally NOT wired here. The mock has no
// VITE_GOOGLE_MAPS_KEY and a non-functional autocomplete bar at the top of
// the panel reads as theatre. Manual fields, well-spaced, in a tight
// 2-column grid that fits a 13" laptop without scroll.

interface LocationFormErrors {
  street?: string
  city?: string
  state?: string
  zipCode?: string
  phoneNumber?: string
}

const COUNTRY = "United States"

export function CompanyLocationStep() {
  const {
    goBack,
    goNext,
    selectedType,
    selectedRole,
    companyDraft,
    setCompanyDraft,
    setCompanySnapshot,
    createdTeamId,
    setCreatedTeamId,
  } = useOnboarding()

  // Step-local fields seeded from the context draft so back-nav preserves
  // every keystroke. The canonical write-through happens on each onChange.
  const [street, setStreet] = useState(companyDraft.street)
  const [city, setCity] = useState(companyDraft.city)
  const [state, setState] = useState(companyDraft.state)
  const [zipCode, setZipCode] = useState(companyDraft.zipCode)
  const [phoneNumber, setPhoneNumber] = useState(companyDraft.phoneNumber)
  const [errors, setErrors] = useState<LocationFormErrors>({})
  const [submitting, setSubmitting] = useState(false)

  const writeBack = (patch: Partial<typeof companyDraft>) => setCompanyDraft(patch)

  const updateStreet = (v: string) => {
    setStreet(v)
    writeBack({ street: v })
    if (errors.street) setErrors((e) => ({ ...e, street: undefined }))
  }
  const updateCity = (v: string) => {
    setCity(v)
    writeBack({ city: v })
    setCompanySnapshot({ name: companyDraft.name, city: v, state })
    if (errors.city) setErrors((e) => ({ ...e, city: undefined }))
  }
  const updateState = (v: string) => {
    setState(v)
    writeBack({ state: v })
    setCompanySnapshot({ name: companyDraft.name, city, state: v })
    if (errors.state) setErrors((e) => ({ ...e, state: undefined }))
  }
  const updateZip = (v: string) => {
    const formatted = formatZip(v)
    setZipCode(formatted)
    writeBack({ zipCode: formatted })
    if (errors.zipCode) setErrors((e) => ({ ...e, zipCode: undefined }))
  }
  const updatePhone = (v: string) => {
    const formatted = formatPhone(v)
    setPhoneNumber(formatted)
    writeBack({ phoneNumber: formatted })
    if (errors.phoneNumber) setErrors((e) => ({ ...e, phoneNumber: undefined }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const parsed = companyLocationSchema.safeParse({
      street,
      city,
      state,
      zipCode,
      country: COUNTRY,
      phoneNumber: phoneNumber || undefined,
    })
    if (!parsed.success) {
      const fieldErrors: LocationFormErrors = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof LocationFormErrors
        if (!fieldErrors[key]) fieldErrors[key] = issue.message
      }
      setErrors(fieldErrors)
      return
    }

    // Defensive guard for the rare race where someone deep-links into
    // /onboarding mid-flow with `selectedType` cleared — bounce to step 2a.
    if (!selectedType || !companyDraft.name) {
      goBack()
      return
    }

    // If the team was already created in a prior submit (unlikely but
    // possible after a transient API error + retry), don't double-create —
    // just continue. The createdTeamId is the source of truth.
    if (createdTeamId) {
      goNext()
      return
    }

    setErrors({})
    setSubmitting(true)
    try {
      const team = await teamsApi.create({
        name: companyDraft.name,
        type: selectedType as TeamType,
        street: parsed.data.street,
        city: parsed.data.city,
        state: parsed.data.state,
        zipCode: parsed.data.zipCode,
        country: parsed.data.country,
        phoneNumber: parsed.data.phoneNumber || undefined,
        ownerRole: selectedRole ?? undefined,
      })
      setCreatedTeamId(team.id)
      setCompanySnapshot({
        name: companyDraft.name,
        city: parsed.data.city,
        state: parsed.data.state,
      })
      goNext()
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't create your team"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  // Address has different *meaning* per org type. For dealerships and body
  // shops it's the operating storefront — where customers go. For groups
  // it's the holding company's HQ; per-location addresses come later when
  // dealerships are added in step 5. The copy reflects that.
  const isGroup = selectedType === "DealershipGroup"
  const title = isGroup ? "Where is your group's HQ?" : "Where is your business based?"
  const subtitle = isGroup
    ? "Just the group's headquarters — where invoicing and legal documents go. You'll add per-dealership addresses in the next step."
    : "The address customers see, and where we'll route invoicing. You can change it anytime in Settings."

  return (
    <StepLayout
      eyebrow="Step 3 — Where you operate"
      title={title}
      subtitle={subtitle}
      hero={<OnboardingContextPanel />}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Future: address autocomplete. Drops in here above the manual
            grid; populates street/city/state/zip/country in one shot.
            Manual fields stay so the user can correct an autocompletion
            and so it works without an API key in dev. */}
        <StaggerField index={0}>
          <Field label="Street address" error={errors.street} required>
            <div className="relative">
              <MarkerPin01
                className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-zinc-400"
                aria-hidden
              />
              <input
                value={street}
                onChange={(e) => updateStreet(e.target.value)}
                maxLength={255}
                placeholder="123 Main Street"
                className={cx(
                  onboardingInputClass,
                  "pl-10",
                  errors.street && onboardingInputErrorClass,
                )}
                autoFocus
              />
            </div>
          </Field>
        </StaggerField>

        <StaggerField index={1}>
          <div className="grid grid-cols-3 gap-3">
            <Field label="City" error={errors.city} required>
              <input
                value={city}
                onChange={(e) => updateCity(e.target.value)}
                maxLength={255}
                placeholder="Springfield"
                className={cx(onboardingInputClass, errors.city && onboardingInputErrorClass)}
              />
            </Field>
            <Field label="State" error={errors.state} required>
              <USStateSelect
                value={state}
                onChange={(e) => updateState(e.target.value)}
                className={cx(
                  onboardingInputClass,
                  "appearance-none cursor-pointer pr-8",
                  errors.state && onboardingInputErrorClass,
                )}
              />
            </Field>
            <Field label="ZIP" error={errors.zipCode} required>
              <input
                value={zipCode}
                onChange={(e) => updateZip(e.target.value)}
                inputMode="numeric"
                maxLength={10}
                placeholder="62701"
                className={cx(onboardingInputClass, errors.zipCode && onboardingInputErrorClass)}
              />
            </Field>
          </div>
        </StaggerField>

        <StaggerField index={2}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Country">
              <div
                aria-disabled
                className={cx(
                  onboardingInputClass,
                  "flex items-center text-zinc-500 cursor-not-allowed bg-zinc-50",
                )}
              >
                {COUNTRY}
              </div>
            </Field>
            <Field label="Phone (optional)" error={errors.phoneNumber}>
              <input
                type="tel"
                inputMode="tel"
                value={phoneNumber}
                onChange={(e) => updatePhone(e.target.value)}
                maxLength={20}
                placeholder="(555) 123-4567"
                className={cx(
                  onboardingInputClass,
                  errors.phoneNumber && onboardingInputErrorClass,
                )}
              />
            </Field>
          </div>
        </StaggerField>

        <StaggerField index={3}>
          <StepActions
            onBack={goBack}
            primary={
              <PrimaryButton loading={submitting} variant="emerald">
                Create workspace
                <ArrowRight className="size-[18px]" aria-hidden />
              </PrimaryButton>
            }
          />
        </StaggerField>
      </form>
    </StepLayout>
  )
}
